import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Minus, CreditCard, RefreshCw } from 'lucide-react';
import { DEV_MOCK, MOCK_SEAT_INFO } from './cancellation/mock';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';
import useOrgStore from '../../../application/store/zustand/useOrgStore';
import {
  PaymentMethod,
  getPaymentMethods,
  createSetupSession,
  removePaymentMethod,
  setDefaultPaymentMethod,
  SeatInfo,
  getSeatInfo,
  getProrationPreview,
  addSeat,
  Transaction,
  getTransactions,
} from '../../../application/routes/settings/components/PaymentMethods/paymentApi';
import {
  formatSeatPrice,
  formatMonthlyEquivalent,
  formatProration,
} from '../../../application/routes/settings/components/PaymentMethods/priceFormatUtils';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

// ─── Payment Methods Tab ────────────────────────────────────────────────────

const brandLabels: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

function PaymentMethodCard({
  method,
  onRemove,
  onSetDefault,
  isRemoving,
  isSettingDefault,
}: {
  method: PaymentMethod;
  onRemove: (pmId: string) => void;
  onSetDefault: (pmId: string) => void;
  isRemoving: boolean;
  isSettingDefault: boolean;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const brandLabel = brandLabels[method.brand] || method.brand;
  const expiry = `${String(method.expMonth).padStart(2, '0')}/${String(
    method.expYear
  ).slice(-2)}`;

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-4 p-4 border border-border rounded-xl bg-card">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="size-10 rounded-lg bg-muted flex items-center justify-center">
            <CreditCard className="size-5 text-muted-foreground" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-foreground">{brandLabel}</span>
            <span className="text-muted-foreground">•••• {method.last4}</span>
            <span className="text-sm text-muted-foreground">
              Expires {expiry}
            </span>
          </div>
          {method.isDefault && (
            <Badge variant="default" className="text-xs">
              Default
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          {!method.isDefault && (
            <Button
              variant="outline"
              size="sm"
              disabled={isSettingDefault}
              onClick={() => onSetDefault(method.id)}
            >
              {isSettingDefault ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                'Set as Default'
              )}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="text-destructive border-destructive/50 hover:bg-destructive/10"
            disabled={isRemoving}
            onClick={() => setConfirmOpen(true)}
          >
            {isRemoving ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              'Remove'
            )}
          </Button>
        </div>
      </div>
      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onRemove(method.id);
        }}
        title={`Remove ${brandLabel} ending in ${method.last4}?`}
        description="This payment method will be permanently removed. This action cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isRemoving}
      />
    </>
  );
}

function PaymentMethodsTab() {
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [addingMethod, setAddingMethod] = useState(false);
  const toast = useToast();

  const orgId: string | undefined = (global as any)?.me?.orgID;

  const fetchMethods = useCallback(async () => {
    if (!orgId) {
      setError('Organization not found');
      setLoading(false);
      return;
    }
    try {
      setError(null);
      setLoading(true);
      const res = await getPaymentMethods(orgId);
      if (res.success && res.data) {
        setMethods(res.data.paymentMethods);
      } else {
        setError(res.error || 'Failed to load payment methods');
      }
    } catch {
      setError('Unable to load payment methods. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchMethods();

    const params = new URLSearchParams(window.location.search);
    if (params.get('setup') === 'success') {
      const url = new URL(window.location.href);
      url.searchParams.delete('setup');
      window.history.replaceState({}, '', url.toString());
    }
  }, [fetchMethods]);

  const handleAddMethod = async () => {
    if (!orgId) {
      return;
    }
    try {
      setAddingMethod(true);
      const currentUrl = window.location.origin + window.location.pathname;
      const res = await createSetupSession(
        orgId,
        `${currentUrl}?setup=success`,
        `${currentUrl}?setup=cancelled`
      );
      if (res.success && res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setError(res.error || 'Failed to start payment setup');
        toast.error('Failed to start payment setup');
        setAddingMethod(false);
      }
    } catch {
      setError('Unable to start payment setup. Please try again.');
      toast.error('Unable to start payment setup');
      setAddingMethod(false);
    }
  };

  const handleRemove = async (pmId: string) => {
    if (!orgId) {
      return;
    }
    try {
      setRemovingId(pmId);
      const res = await removePaymentMethod(orgId, pmId);
      if (res.success) {
        toast.success('Payment method removed');
        await fetchMethods();
      } else {
        setError(res.error || 'Failed to remove payment method');
        toast.error(res.error || 'Failed to remove payment method');
      }
    } catch {
      setError('Unable to remove payment method. Please try again.');
      toast.error('Unable to remove payment method');
    } finally {
      setRemovingId(null);
    }
  };

  const handleSetDefault = async (pmId: string) => {
    if (!orgId) {
      return;
    }
    try {
      setSettingDefaultId(pmId);
      const res = await setDefaultPaymentMethod(orgId, pmId);
      if (res.success) {
        toast.success('Default payment method updated');
        await fetchMethods();
      } else {
        setError(res.error || 'Failed to set default payment method');
        toast.error(res.error || 'Failed to set default payment method');
      }
    } catch {
      setError('Unable to set default payment method. Please try again.');
      toast.error('Unable to set default payment method');
    } finally {
      setSettingDefaultId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="border border-border/60 rounded-xl p-6 space-y-6">
      <h3 className="text-base font-bold text-foreground">Payment Methods</h3>

      {error && (
        <div className="flex items-center justify-between p-4 rounded-lg bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchMethods}>
            Retry
          </Button>
        </div>
      )}

      {methods.length === 0 ? (
        <div className="h-[120px] border border-dashed border-border/60 rounded-[20px]" />
      ) : (
        <div className="space-y-3">
          {methods.map((m) => (
            <PaymentMethodCard
              key={m.id}
              method={m}
              onRemove={handleRemove}
              onSetDefault={handleSetDefault}
              isRemoving={removingId === m.id}
              isSettingDefault={settingDefaultId === m.id}
            />
          ))}
        </div>
      )}

      <Button
        variant="outline"
        onClick={handleAddMethod}
        disabled={addingMethod}
        className="h-12 px-4 gap-2.5 rounded-lg border-border font-semibold text-base"
      >
        {addingMethod ? (
          <Loader2 className="size-6 animate-spin" />
        ) : (
          <RefreshCw className="size-6" />
        )}
        Add Payment Method
      </Button>
    </div>
  );
}

// ─── Transactions Tab ───────────────────────────────────────────────────────

const statusStyles: Record<string, string> = {
  succeeded:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  canceled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  requires_payment_method:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  requires_action:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  processing: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

function TransactionsTab() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId: string | undefined = (global as any)?.me?.orgID;

  const fetchTransactions = useCallback(async () => {
    if (!orgId) {
      setError('Organization not found');
      setLoading(false);
      return;
    }
    try {
      setError(null);
      setLoading(true);
      const res = await getTransactions(orgId);
      if (res.success && res.data) {
        setTransactions(res.data.transactions);
      } else {
        setError(res.error || 'Failed to load transactions');
      }
    } catch {
      setError('Unable to load transactions. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="flex items-center justify-between p-4 rounded-lg bg-destructive/10 border border-destructive/20 mb-4">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchTransactions}>
            Retry
          </Button>
        </div>
      )}

      {transactions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 border border-dashed border-border rounded-xl">
          <p className="text-base font-medium text-muted-foreground mb-1">
            No transactions yet
          </p>
          <p className="text-sm text-muted-foreground">
            Transactions will appear here after estimates are purchased.
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left font-medium text-muted-foreground px-4 py-3">
                    Date
                  </th>
                  <th className="text-left font-medium text-muted-foreground px-4 py-3">
                    Description
                  </th>
                  <th className="text-right font-medium text-muted-foreground px-4 py-3">
                    Amount
                  </th>
                  <th className="text-left font-medium text-muted-foreground px-4 py-3">
                    Status
                  </th>
                  <th className="text-left font-medium text-muted-foreground px-4 py-3">
                    Receipt
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className="border-b border-border last:border-b-0 hover:bg-muted/30"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-foreground">
                      {formatDate(tx.created)}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {tx.description}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap text-foreground font-medium">
                      ${tx.amount.toFixed(2)} {tx.currency.toUpperCase()}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium',
                          statusStyles[tx.status] || 'bg-gray-100 text-gray-800'
                        )}
                      >
                        {tx.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {tx.receiptUrl ? (
                        <a
                          href={tx.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-muted-foreground">&mdash;</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Subscription Tab ───────────────────────────────────────────────────────

function SubscriptionTab() {
  const navigate = useNavigate();
  const [seatInfo, setSeatInfo] = useState<SeatInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noSubscription, setNoSubscription] = useState(false);

  // Purchase dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [prorationAmount, setProrationAmount] = useState<number | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [seatQuantity, setSeatQuantity] = useState(1);

  const toast = useToast();
  const orgId: string | undefined = (global as any)?.me?.orgID;

  const fetchSeatInfo = useCallback(async () => {
    if (DEV_MOCK) {
      setSeatInfo(MOCK_SEAT_INFO as unknown as SeatInfo);
      setLoading(false);
      return;
    }
    if (!orgId) {
      setError('Organization not found');
      setLoading(false);
      return;
    }
    try {
      setError(null);
      setNoSubscription(false);
      setLoading(true);
      const res = await getSeatInfo(orgId);
      if (res.success && res.data) {
        setSeatInfo(res.data);
      } else if (res.error === 'NO_SUBSCRIPTION') {
        setNoSubscription(true);
      } else {
        setError(res.error || 'Failed to load subscription info');
      }
    } catch {
      setError('Unable to load subscription info. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchSeatInfo();
  }, [fetchSeatInfo]);

  const handleOpenDialog = async () => {
    if (!orgId) {
      return;
    }
    setDialogOpen(true);
    setSeatQuantity(1);
    await fetchProration(1);
  };

  const fetchProration = async (qty: number) => {
    if (!orgId) {
      return;
    }
    setDialogLoading(true);
    setDialogError(null);
    setProrationAmount(null);
    try {
      const res = await getProrationPreview(orgId, qty);
      if (res.success && res.data) {
        setProrationAmount(res.data.prorationAmount);
      } else {
        setDialogError(res.error || 'Failed to load proration preview');
      }
    } catch {
      setDialogError('Unable to load pricing. Please try again.');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleQuantityChange = (newQty: number) => {
    if (newQty < 1) {
      return;
    }
    setSeatQuantity(newQty);
    fetchProration(newQty);
  };

  const handleConfirmPurchase = async () => {
    if (!orgId) {
      return;
    }
    try {
      setPurchasing(true);
      setDialogError(null);
      const res = await addSeat(orgId, seatQuantity);
      if (res.success) {
        setDialogOpen(false);
        toast.success('Seats purchased successfully');
        await fetchSeatInfo();
      } else {
        setDialogError(res.error || 'Failed to purchase seat');
      }
    } catch {
      setDialogError('Unable to complete purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (noSubscription) {
    return (
      <div className="flex flex-col items-center justify-center py-12 border border-dashed border-border rounded-xl">
        <p className="text-base font-medium text-muted-foreground mb-1">
          No active subscription
        </p>
        <p className="text-sm text-muted-foreground">
          Your organization does not have a seat-based subscription.
        </p>
      </div>
    );
  }

  const usagePercent =
    seatInfo && seatInfo.purchasedSeats > 0
      ? (seatInfo.activeUsers / seatInfo.purchasedSeats) * 100
      : 0;

  const getStatusLabel = () => {
    if (!seatInfo) {
      return '';
    }
    if (seatInfo.cancelAtPeriodEnd) {
      return 'Canceling';
    }
    if (seatInfo.subscriptionStatus === 'trialing') {
      return 'Free Trial';
    }
    if (seatInfo.subscriptionStatus === 'active') {
      return 'Active';
    }
    return (
      seatInfo.subscriptionStatus.charAt(0).toUpperCase() +
      seatInfo.subscriptionStatus.slice(1)
    );
  };

  const getStatusColor = () => {
    if (!seatInfo) {
      return '';
    }
    if (seatInfo.cancelAtPeriodEnd) {
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    }
    if (seatInfo.subscriptionStatus === 'trialing') {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
    if (seatInfo.subscriptionStatus === 'active') {
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    }
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-center justify-between p-4 rounded-lg bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchSeatInfo}>
            Retry
          </Button>
        </div>
      )}

      {seatInfo && seatInfo.customerType === 'fixed_rate' && (
        <p className="text-muted-foreground">
          Your organization is on a fixed-rate plan with uncapped seats.
        </p>
      )}

      {seatInfo && seatInfo.customerType === 'seat_based' && (
        <>
          {/* Trial banner */}
          {seatInfo.subscriptionStatus === 'trialing' &&
            seatInfo.trialEndDate && (
              <div
                className={cn(
                  'p-4 rounded-lg border',
                  seatInfo.cancelAtPeriodEnd
                    ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800'
                    : 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800'
                )}
              >
                <p className="font-semibold text-sm text-foreground">
                  {seatInfo.cancelAtPeriodEnd
                    ? 'Your trial has been canceled'
                    : "You're on a free trial"}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {seatInfo.cancelAtPeriodEnd
                    ? `Your access will end on ${formatDate(
                        seatInfo.trialEndDate
                      )}. You will not be charged.`
                    : `Your trial ends on ${formatDate(
                        seatInfo.trialEndDate
                      )}. You will be billed ${formatSeatPrice(
                        seatInfo.pricePerSeat,
                        seatInfo.billingTerm,
                        seatInfo.currency
                      )} (${seatInfo.purchasedSeats} seat${
                        seatInfo.purchasedSeats !== 1 ? 's' : ''
                      }) on that date.`}
                </p>
              </div>
            )}

          {/* Active subscription canceled banner */}
          {seatInfo.subscriptionStatus === 'active' &&
            seatInfo.cancelAtPeriodEnd && (
              <div className="p-4 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800">
                <p className="font-semibold text-sm text-foreground">
                  Your subscription has been canceled
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your access will end on{' '}
                  <strong>
                    {seatInfo.nextBillingDate
                      ? formatDate(seatInfo.nextBillingDate)
                      : ''}
                  </strong>
                  . You will not be charged again.
                </p>
              </div>
            )}

          {/* Seat usage */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                {seatInfo.activeUsers} / {seatInfo.purchasedSeats} seats used
              </span>
              <span
                className={cn(
                  'inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold',
                  getStatusColor()
                )}
              >
                {getStatusLabel()}
              </span>
            </div>
            <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
          </div>

          {/* Subscription details grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 p-4 border border-border rounded-xl bg-card">
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                Minimum seats
              </p>
              <p className="text-sm font-medium text-foreground">
                {seatInfo.minimumSeats}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                Available seats
              </p>
              <p className="text-sm font-medium text-foreground">
                {seatInfo.availableSeats}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Billing term</p>
              <p className="text-sm font-medium text-foreground">
                {seatInfo.billingTerm}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                Price per seat
              </p>
              <p className="text-sm font-medium text-foreground">
                {formatSeatPrice(
                  seatInfo.pricePerSeat,
                  seatInfo.billingTerm,
                  seatInfo.currency
                )}
              </p>
              {formatMonthlyEquivalent(
                seatInfo.pricePerSeat,
                seatInfo.billingTerm,
                seatInfo.currency
              ) && (
                <p className="text-xs text-muted-foreground">
                  {formatMonthlyEquivalent(
                    seatInfo.pricePerSeat,
                    seatInfo.billingTerm,
                    seatInfo.currency
                  )}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {seatInfo.subscriptionStatus === 'trialing'
                  ? 'Trial ends'
                  : 'Next billing date'}
              </p>
              <p className="text-sm font-medium text-foreground">
                {seatInfo.subscriptionStatus === 'trialing' &&
                seatInfo.trialEndDate
                  ? formatDate(seatInfo.trialEndDate)
                  : seatInfo.nextBillingDate
                  ? formatDate(seatInfo.nextBillingDate)
                  : '\u2014'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Status</p>
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold',
                  getStatusColor()
                )}
              >
                {getStatusLabel()}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 flex-wrap">
            {!seatInfo.cancelAtPeriodEnd && (
              <Button onClick={handleOpenDialog}>
                Purchase Additional Seats
              </Button>
            )}
            {seatInfo.selfServiceCancellation &&
              !seatInfo.cancelAtPeriodEnd && (
                <Button
                  variant="outline"
                  className="text-destructive border-destructive/50 hover:bg-destructive/10"
                  onClick={() => navigate('/v2/settings/billing/cancel')}
                >
                  {seatInfo.subscriptionStatus === 'trialing'
                    ? 'Cancel Trial'
                    : 'Cancel Subscription'}
                </Button>
              )}
          </div>
          {!seatInfo.selfServiceCancellation && (
            <p className="text-sm text-muted-foreground">
              To modify or cancel your subscription, contact your account
              representative.
            </p>
          )}
        </>
      )}

      {/* Purchase seats dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!purchasing) {
            setDialogOpen(open);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase Additional Seats</DialogTitle>
            <DialogDescription>
              Add more seats to your subscription.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-3 my-4">
            <span className="text-sm text-muted-foreground">Seats to add:</span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() => handleQuantityChange(seatQuantity - 1)}
                disabled={seatQuantity <= 1 || dialogLoading || purchasing}
              >
                <Minus className="size-4" />
              </Button>
              <Input
                value={seatQuantity}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1) {
                    handleQuantityChange(val);
                  }
                }}
                className="w-16 text-center"
                disabled={dialogLoading || purchasing}
              />
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() => handleQuantityChange(seatQuantity + 1)}
                disabled={dialogLoading || purchasing}
              >
                <Plus className="size-4" />
              </Button>
            </div>
          </div>

          {dialogLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : dialogError ? (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive">{dialogError}</p>
            </div>
          ) : prorationAmount != null && seatInfo ? (
            <p className="text-sm text-foreground">
              Adding {seatQuantity} seat{seatQuantity > 1 ? 's' : ''} will cost
              approximately{' '}
              <strong>
                {formatProration(prorationAmount, seatInfo.currency)}
              </strong>{' '}
              for the remainder of this billing cycle.
            </p>
          ) : null}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={purchasing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPurchase}
              disabled={dialogLoading || !!dialogError || purchasing}
            >
              {purchasing ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : null}
              Confirm Purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main Billing Page ──────────────────────────────────────────────────────

export function BillingPage() {
  const navigate = useNavigate();
  const orgData = useOrgStore((state) => state.orgData);
  const isSeatBased = orgData?.billingType === 'seat_based';

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Billing"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-10">
          {/* Section header with icon */}
          <div className="flex items-center gap-6">
            <div className="size-16 rounded-full bg-secondary/20 flex items-center justify-center shrink-0">
              <CreditCard className="size-5 text-secondary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-foreground tracking-tight">
                Billing
              </h2>
              <p className="text-sm text-muted-foreground">
                Manage payment methods and subscription, view transactions, and
                more
              </p>
            </div>
          </div>

          {/* TODO: REMOVE — Temporary dev preview for cancellation flow */}
          {DEV_MOCK && (
            <div className="p-4 rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-900/10 space-y-2">
              <p className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide">
                Dev Preview — Remove before merge
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Mock mode: <strong>{DEV_MOCK ? 'ON' : 'OFF'}</strong> | Billing
                term: <strong>{MOCK_SEAT_INFO.billingTerm}</strong> | Plan:{' '}
                <strong>{MOCK_SEAT_INFO.planType}</strong> (change in
                cancellation/mock.ts)
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/v2/settings/billing/cancel')}
                >
                  Preview: Cancellation Gate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate('/v2/settings/billing/cancel/confirmed')
                  }
                >
                  Preview: Cancellation Confirmed
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/v2/settings/billing/cancel/survey')}
                >
                  Preview: Exit Survey
                </Button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="payment-methods" className="w-full">
            <TabsList className="w-full bg-transparent border-b border-border rounded-none p-0 h-auto gap-6">
              <TabsTrigger
                value="payment-methods"
                className="flex-1 rounded-none border-b-[3px] border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground px-0 pb-3 pt-2 font-bold text-sm text-center"
              >
                Payment Methods
              </TabsTrigger>
              <TabsTrigger
                value="transactions"
                className="flex-1 rounded-none border-b-[3px] border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground px-0 pb-3 pt-2 font-bold text-sm text-center"
              >
                Transactions
              </TabsTrigger>
              {(isSeatBased || DEV_MOCK) && (
                <TabsTrigger
                  value="subscription"
                  className="flex-1 rounded-none border-b-[3px] border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground px-0 pb-3 pt-2 font-bold text-sm text-center"
                >
                  Subscription
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="payment-methods" className="mt-6">
              <PaymentMethodsTab />
            </TabsContent>

            <TabsContent value="transactions" className="mt-6">
              <TransactionsTab />
            </TabsContent>

            {(isSeatBased || DEV_MOCK) && (
              <TabsContent value="subscription" className="mt-6">
                <SubscriptionTab />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
